import React, { useRef, useState, useEffect, useMemo } from "react";
import "../common/AddNewLicensePanel.css";
import Icon from "../../../common/Icon";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { getUserDetails } from "../../../../backend/auth";
import {
  useCreateLicenseApplication,
  useGetLicenseTypes,
  useGetApplicationTypes,
} from "../../../../backend/license";
import { useGetUserCompanies } from "../../../../backend/query";
import {
  useGetStates,
  useGetCities,
} from "../../../../backend/masters";
import { useGetUsers } from "../../../../backend/users";

interface SidePanelProps {
  isOpen: boolean;
  closePanel: () => void;
  onSuccess?: () => void;
}

const NewApplicationPanel: React.FC<SidePanelProps> = ({
  isOpen,
  closePanel,
  onSuccess,
}) => {
  const user = getUserDetails();
  const [currentStep, setCurrentStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    company_id: "",
    associate_company_id: "",
    location_id: "",
    license_type_id: "",
    state_id: "",
    city_id: "",
    license_name: "",
    authority: "",
    application_type_id: "",
    assigned_to_id: "",
    requested_by_id: "",
    editor_ids: [],
    watcher_ids: [] as (string | number)[],
    status: "not_started",
    description: "",
    // license_reference_id: "",
  });

  const resetForm = () => {
    setFormData({
      company_id: "",
      associate_company_id: "",
      location_id: "",
      license_type_id: "",
      state_id: "",
      city_id: "",
      license_name: "",
      authority: "",
      application_type_id: "",
      assigned_to_id: "",
      requested_by_id: "",
      editor_ids: [],
      watcher_ids: [],
      status: "not_started",
      description: "",
    });
    setCurrentStep(1);
    setAssociateCompanies([]);
    setLocations([]);
    setUploadedFiles([]);
  };

  // Handle close panel with reset
  const handleClosePanel = () => {
    resetForm();
    closePanel();
  };

  const { userCompanies, isFetching: loadingCompanies } = useGetUserCompanies(
    {
      include: [
        "associate_companies",
        "associate_companies.locations",
        "associate_companies.locations.city",
        "associate_companies.locations.state"
      ],
      includes: [
        "associate_companies",
        "associate_companies.locations",
        "associate_companies.locations.city",
        "associate_companies.locations.state"
      ],
    },
    true
  );

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  // Process companies
  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      setCompanies(userCompanies);
    }
  }, [userCompanies]);

  // Cascade: Company → Associate Companies
  useEffect(() => {
    if (formData.company_id && companies.length > 0) {
      const selectedCompany = companies.find(
        (c: any) => c.id === formData.company_id
      );
      if (selectedCompany && selectedCompany.associate_companies) {
        setAssociateCompanies(selectedCompany.associate_companies);
      } else {
        setAssociateCompanies([]);
      }
      // Reset dependent fields
      setFormData((prev: any) => ({
        ...prev,
        associate_company_id: "",
        location_id: "",
      }));
      setLocations([]);
    } else {
      setAssociateCompanies([]);
    }
  }, [formData.company_id, companies]);

  // Cascade: Associate Company → Locations
  useEffect(() => {
    if (formData.associate_company_id && associateCompanies.length > 0) {
      const selectedAssocCompany = associateCompanies.find(
        (ac: any) => ac.id === formData.associate_company_id
      );
      if (selectedAssocCompany && selectedAssocCompany.locations) {
        setLocations(selectedAssocCompany.locations);
      } else {
        setLocations([]);
      }
      // Reset location
      setFormData((prev: any) => ({
        ...prev,
        location_id: "",
      }));
    } else {
      setLocations([]);
    }
  }, [formData.associate_company_id, associateCompanies]);

  useEffect(() => {
    if (formData.location_id && locations.length > 0) {
      const selectedLocation = locations.find(
        (loc: any) => loc.id === formData.location_id
      );

      if (selectedLocation) {
        setFormData((prev: any) => ({
          ...prev,
          state_id: selectedLocation.state.id || "",
          city_id: selectedLocation.city.id || "",
        }));
      }
    } else {
      setFormData((prev: any) => ({
        ...prev,
        state_id: "",
        city_id: "",
      }));
    }
  }, [formData.location_id, locations]);

  const { licenseTypes: allLicenseTypes } = useGetLicenseTypes(
    {
      scopes: {
        // Use state-aware scope when state is available
        available_for_location: formData.associate_company_id && formData.state_id
        ? [formData.associate_company_id, formData.state_id]
          : null,
      },
      include: ['application_types'],
      includes: ['application_types'],
    },
    Boolean(formData.associate_company_id && formData.state_id)
  );
  const licenseTypes = useMemo(() => {
    if (!allLicenseTypes || allLicenseTypes.length === 0) return [];
    const filtered = allLicenseTypes.filter((licenseType: any) => {
      const appTypes = licenseType.application_types || [];
      console.log(`License Type: ${licenseType.name}, Application Types:`, appTypes);

      const hasNewType = appTypes.some((appType: any) =>
        appType.action_type === 'new' || appType.action_type === 'New'
      );
      const matchesCity = !licenseType.city_id ||
                          !formData.city_id ||
                          licenseType.city_id === formData.city_id;
      return hasNewType && matchesCity;
    });
    return filtered;
  }, [allLicenseTypes, formData.city_id]);
  const { states } = useGetStates({}, true);
  const { cities } = useGetCities(
    { filters: { state_id_eq: formData.state_id } },
    Boolean(formData.state_id)
  );
  const { applicationTypes, isFetching: isFetchingApplicationTypes } = useGetApplicationTypes(
    { filters: { license_type_id_eq: formData.license_type_id } },
    Boolean(formData.license_type_id)
  );
  const { records: licenseOwners } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Owner'] 
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );
  const { records: licenseWatchers } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Watcher']  
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );
  
  // Auto-select "New" application type when license_type is selected
   useEffect(() => {
    // Wait for applicationTypes to finish loading before attempting auto-selection
    if (isFetchingApplicationTypes) {
      console.log('Waiting for applicationTypes to load...');
      return;
    }

    if (formData.license_type_id && applicationTypes && applicationTypes.length > 0) {
      const newAppType = applicationTypes.find(
        (at: any) => at.action_type === 'new' || at.action_type === 'New'
      );

      if (newAppType && !formData.application_type_id) {
        console.log('Auto-selecting "New" application type:', newAppType.id);
        setFormData((prev: any) => ({
          ...prev,
          application_type_id: newAppType.id,
        }));
      } else if (!newAppType) {
        console.warn('No "New" application type found for license_type_id:', formData.license_type_id);
      }
    }
  }, [formData.license_type_id, applicationTypes, isFetchingApplicationTypes]);

  function getLastHyphenValue(str: string | null | undefined): string {
    if (!str) return "";
    const parts = str.split("-");
    return parts[parts.length - 1].trim();
  }

  // File upload
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Create mutation
  const { createApplication, creating } = useCreateLicenseApplication(
    (response: any) => {
      (response &&
      toast.success("New license application created successfully", {
        icon: <Icon name="snackbarSuccess" />,
      }))
      resetForm();
      closePanel();
      onSuccess && onSuccess();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && errorData.error_messages;

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to create license application");
      }
    }
  );

  // Auto-fill authority when license type is selected
  useEffect(() => {
    if (formData.license_type_id && licenseTypes) {
      const selectedType = licenseTypes.find(
        (lt: any) => lt.id === formData.license_type_id
      );
      if (selectedType) {
        setFormData((prev: any) => ({
          ...prev,
          authority: (selectedType && selectedType.authority) || "",
        }));
      }
    }
  }, [formData.license_type_id, licenseTypes]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateStep1 = () => {
    if (!formData.company_id) {
      toast.error("Please select a company");
      return false;
    }
    if (!formData.associate_company_id) {
      toast.error("Please select an associate company");
      return false;
    }
    if (!formData.location_id) {
      toast.error("Please select a company location");
      return false;
    }
    if (!formData.state_id) {
      toast.error("Please select a state");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.license_name) {
      toast.error("Please enter license name");
      return false;
    }
    if (!formData.license_type_id) {
      toast.error("Please select a license type");
      return false;
    }
    if (!formData.authority) {
      toast.error("Authority is required");
      return false;
    }
    // if (!formData.application_type_id) {
    //   toast.error("Please select application type");
    //   return false;
    // }
    return true;
  };

  const validateStep3 = () => {
    // if (!formData.issue_date) {
    //   toast.error("Please select issue date");
    //   return false;
    // }
    // if (!formData.is_perpetual && !formData.expiry_date) {
    //   toast.error("Please select expiry date or mark as perpetual");
    //   return false;
    // }
    // if (!formData.is_perpetual && formData.expiry_date && formData.issue_date) {
    //   if (new Date(formData.expiry_date) <= new Date(formData.issue_date)) {
    //     toast.error("Expiry date must be after issue date");
    //     return false;
    //   }
    // }
    if (!formData.assigned_to_id) {
      toast.error("Please assign to a user");
      return false;
    }
    if (!formData.watcher_ids.length) {
      toast.error("Watcher is required.");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async () => {
    if (!validateStep3()) return;

    const { state_id, city_id, ...validData } = formData;

    const payload = {
    license_application: {
      company_id: formData.company_id,
      associate_company_id: formData.associate_company_id,
      location_id: formData.location_id,
      license_type_id: formData.license_type_id,
      state_id: formData.state_id,
      city_id: formData.city_id,
      license_name: formData.license_name,
      authority: formData.authority,
      // application_type_id: formData.application_type_id,
      assigned_to_id: formData.assigned_to_id,
      requested_by_id: user.user_id,
      // editor_ids: formData.editor_ids.length > 0 ? formData.editor_ids : [user.user_id],
      watcher_ids: formData.watcher_ids,
      status: formData.status,
      description: formData.description,
      // license_reference_id: formData.license_reference_id, // ✅ Correct field name
      created_by_id: user.user_id,
      updated_by_id: user.user_id,
    },
  };


    createApplication(payload as any);
  };


  return (
    <>
      {isOpen && <div className="sidepanel-overlay" onClick={handleClosePanel}></div>}
      <div className="sidepanel-wrapper">
        <div className={`sidepanel ${isOpen ? "open" : ""}`}>
          <div className="sidepanel-header d-flex justify-content-between align-items-center pb-4">
            <h5 className="mb-0 fw-semibold">Create New Application</h5>
            <button
              className="btn-close"
              aria-label="Close"
              onClick={handleClosePanel}
            ></button>
          </div>

          <div className="sidepanel-body h-100">
            <ul className="progress-section p-0 m-0 pl-2">
              <li
                className={currentStep === 1 || currentStep > 1 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 1 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 1</p>
                <h6>Basic Details</h6>
              </li>
              <li
                className={currentStep === 2 || currentStep > 2 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 2 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 2</p>
                <h6>License Details</h6>
              </li>
              <li className={currentStep === 3 ? "active" : ""}>
                <div className="progress-circle d-flex justify-content-center align-items-center">
                  <div className="progress-checked"></div>
                </div>
                <p>Step 3</p>
                <h6>Assignment</h6>
              </li>
            </ul>

            <hr className="my-4" />

            {/* Step 1 - Basic Details */}
            {currentStep === 1 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    Company<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.company_id}
                    onChange={(e) =>
                      handleInputChange("company_id", e.target.value)
                    }
                  >
                    <option value="">Select company</option>
                    {(companies || []).map((company: any) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Associate Company<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.associate_company_id}
                    onChange={(e) =>
                      handleInputChange("associate_company_id", e.target.value)
                    }
                    disabled={!formData.company_id}
                  >
                    <option value="">Select associate company</option>
                    {(associateCompanies || []).map((company: any) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Company Location<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.location_id}
                    onChange={(e) =>
                      handleInputChange("location_id", e.target.value)
                    }
                    disabled={!formData.associate_company_id}
                  >
                    <option value="">Select company location</option>
                    {(locations || []).map((location: any) => (
                      <option key={location.id} value={location.id}>
                        {location.name} - {getLastHyphenValue(location.company_location_address)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    State<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.state_id}
                    onChange={(e) =>
                      handleInputChange("state_id", e.target.value)
                    }
                    disabled
                  >
                    <option value="">Select state</option>
                    {(states || []).map((state: any) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    City
                  </label>
                  <select
                    className="form-select"
                    value={formData.city_id}
                    onChange={(e) =>
                      handleInputChange("city_id", e.target.value)
                    }
                    disabled
                  >
                    <option value="">Select city</option>
                    {(cities || []).map((city: any) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            )}

            {/* Step 2 - License Details */}
            {currentStep === 2 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    License Name<span>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter license name"
                    value={formData.license_name}
                    onChange={(e) =>
                      handleInputChange("license_name", e.target.value)
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    License Type<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.license_type_id}
                    onChange={(e) =>
                      handleInputChange("license_type_id", e.target.value)
                    }
                  >
                    <option value="">Select license type</option>
                    {(licenseTypes || []).map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* <div className="mb-3">
                  <label className="form-label">
                    Application Type<span>*</span>
                  </label>
                  <select
                    className="form-select text-capitalize"
                    value={formData.application_type_id}
                    onChange={(e) =>
                      handleInputChange("application_type_id", e.target.value)
                    }
                  >
                    <option value="">Select application type</option>
                    {(applicationTypes || []).map((type: any) => (
                      <option key={type.id} value={type.id} className="text-capitalize">
                        {type.action_type}
                      </option>
                    ))}
                  </select>
                </div> */}

                {/* Application Type - Auto-selected to "New", hidden from user */}
                <input
                  type="hidden"
                  name="application_type_id"
                  value={formData.application_type_id || ''}
                />


                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    id="description"
                    placeholder="Add description"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Issuing Authority<span>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Authority"
                    value={formData.authority}
                    onChange={(e) =>
                      handleInputChange("authority", e.target.value)
                    }
                  />
                </div>

                {/* <div className="mb-3">
                  <label className="form-label">
                    Application Type<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.application_type_id}
                    onChange={(e) =>
                      handleInputChange("application_type_id", e.target.value)
                    }
                    disabled={!formData.license_type_id}
                  >
                    <option value="">Select application type</option>
                    {(applicationTypes || []).map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.action_type &&
                          type.action_type.replace(/_/g, " ").toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div> */}

                {/* <div className="mb-3">
                  <label className="form-label">Reference License</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Add Reference license"
                    value={formData.license_reference_id}
                    onChange={(e) =>
                      handleInputChange("license_reference_id", e.target.value)
                    }
                  />
                </div> */}
              </form>
            )}

            {/* Step 3 - Assignment */}
            {currentStep === 3 && (
              <form>
                {/* {hasUserAccess(USER_PRIVILEGES.ASSIGN_LICENSE_OWNER) && ( */}
                <div className="mb-3">
                  <label className="form-label">
                    Assigned to<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.assigned_to_id}
                    onChange={(e) =>
                      handleInputChange("assigned_to_id", e.target.value)
                    }
                  >
                    <option value="">Select an owner who will manage this license end to end</option>
                    {(licenseOwners || []).map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* {hasUserAccess(USER_PRIVILEGES.ASSIGN_LICENSE_WATCHER) && ( */}
                <div className="mb-3">
                  <label className="form-label">Watchers<span>*</span></label>
                  <div
                    className="watchers-box p-3"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {(licenseWatchers || []).map((watcher: any) => (
                      <div
                        key={watcher.id}
                        className="mb-3 d-flex gap-2 align-items-start"
                      >
                        <input
                          className="form-check"
                          type="checkbox"
                          id={`watcher-${watcher.id}`}
                          checked={formData.watcher_ids.includes(watcher.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData((prev: any) => ({
                              ...prev,
                              watcher_ids: checked
                                ? [...prev.watcher_ids, watcher.id]
                                : prev.watcher_ids.filter(
                                    (id: string) => id !== watcher.id
                                  ),
                            }));
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`watcher-${watcher.id}`}
                        >
                          <div className="fs-6">{watcher.name}</div>
                          <small className="text-sm">{watcher.email}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>              
              </form>
            )}
          </div>
        </div>

        <div className={`button-container ${isOpen ? "open" : ""}`}>
          {currentStep === 1 && (
            <div className="w-100">
              <button
                type="button"
                className="button-primary w-100"
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          )}
          {currentStep === 2 && (
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100"
                onClick={handleBack}
              >
                Back
              </button>
              <button
                type="button"
                className="button-primary w-100"
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          )}
          {currentStep === 3 && (
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100"
                onClick={handleBack}
              >
                Back
              </button>
              <button
                type="button"
                className="button-primary w-100"
                onClick={onSubmit}
                disabled={creating}
              >
                {creating ? "Saving..." : "Done"}
              </button>
            </div>
          )}
        </div>
      </div>
      {creating && <PageLoader />}
    </>
  );
};

export default NewApplicationPanel;
